/**
 * paywall.js — global module that turns any HTTP 402 response into a
 * polished upgrade prompt.
 *
 * How it works
 * ------------
 * The backend `shared/usage_gates.py` returns HTTP 402 with a structured
 * payload:
 *   {
 *     error:        'tier_limit_reached' | 'tier_feature_locked' | 'tier_required',
 *     current_tier: 'free' | 'student' | 'scholar' | 'pro',
 *     limit_kind?:  string,    // e.g. 'AI Study Buddy sessions'
 *     feature?:     string,    // for boolean gates
 *     current?:     number,
 *     limit?:       number | 'unlimited',
 *     required_tier?: 'student' | 'scholar' | 'pro',
 *     reason:       string,    // human-readable explanation
 *   }
 *
 * The axios response interceptor (installed via ``installPaywallInterceptor``)
 * catches that shape and emits a ``paywall:open`` event with the payload.
 * <PaywallModal /> listens for the event and renders the right upgrade copy.
 *
 * No prop-drilling, no context wrapping — the interceptor runs at the
 * network layer and the modal listens at the React root.
 */
import axios from 'axios';

const PAYWALL_EVENT = 'paywall:open';

/** Fire the upgrade prompt from anywhere in the app. */
export function showPaywall(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PAYWALL_EVENT, { detail }));
}

/** Subscribe to paywall events. Returns an unsubscribe function. */
export function onPaywall(handler) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e) => handler(e.detail || {});
  window.addEventListener(PAYWALL_EVENT, listener);
  return () => window.removeEventListener(PAYWALL_EVENT, listener);
}

let installed = false;

/**
 * Install the global axios response interceptor. Safe to call multiple
 * times — only the first call wires the handler.
 *
 * Recognises three backend shapes:
 *   1. The new 4-tier gates ({error: 'tier_limit_reached', ...})
 *   2. Boolean feature gates ({error: 'tier_feature_locked', ...})
 *   3. Generic 402 with no structured body — fires a default "Upgrade"
 *      prompt so users still see SOMETHING actionable.
 */
export function installPaywallInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 402) {
        const detail = error.response?.data?.detail;
        if (detail && typeof detail === 'object') {
          showPaywall(detail);
        } else {
          showPaywall({
            error: 'tier_required',
            reason: typeof detail === 'string'
              ? detail
              : 'This action requires a paid plan.',
          });
        }
      }
      return Promise.reject(error);
    }
  );
}
